import "./SpaceSettingsPage.css";

import { Button, Callout, Dialog, Field, Icons, LoadingState, Page, Panel, TextArea, TextInput, Toggle, useFeedback } from "../../../../ds";
import { IChangeRequestService, changeRequestService } from "../../../changeRequests/services/ChangeRequestService";
import { ILockService, lockErrorKind, lockErrorMessage, lockService } from "../../../locks/services/LockService";
import { ISpaceService, spaceService } from "../../services/SpaceService";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { SpaceFieldErrors, SpaceFields, changeRequestsLabel, hasErrors, validateSpaceFields } from "../../utils/spaceFields";
import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { IdentityUnavailableState } from "../../../locks/components/LockStates";
import { LockedPagesSection } from "./LockedPagesSection";
import { PageLockDTO } from "../../../locks/models/LockDTO";
import { PermissionsSection } from "./PermissionsSection";
import { PushPolicySelector } from "../../../changeRequests/components/PushPolicySelector";
import { Space } from "../../models/Space";
import { SpaceState, fetchSpaceState } from "../../utils/spaceState";

export type SettingsSection = "overview" | "change-requests" | "permissions" | "locked-pages" | "danger-zone";

interface ISpaceSettingsPageProps {
    /** Injected in tests; the shared clients otherwise. */
    spaces?: ISpaceService;
    locks?: ILockService;
    changeRequests?: IChangeRequestService;
    /** Scrolls to this section once the space is loaded (the old `edit/permission` route lands on Permissions). */
    focus?: SettingsSection;
}

export type LocksState =
    | { status: "loading" }
    | { status: "ready"; locks: PageLockDTO[] }
    | { status: "identity-unavailable" }
    | { status: "error"; message: string };

interface FormValues extends SpaceFields {
    isHighlighted: boolean;
}

const fieldLabels: Record<keyof FormValues, string> = {
    title: "Title",
    projectId: "Project ID",
    description: "Description",
    isHighlighted: "Highlighted on the Spaces home",
};

const sections: { id: SettingsSection; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "change-requests", label: "Change requests" },
    { id: "permissions", label: "Permissions" },
    { id: "locked-pages", label: "Locked pages" },
    { id: "danger-zone", label: "Danger zone" },
];

const policyHelp = [
    { policy: "propose", text: "Pushes wait as change requests. Ark Owners review the diff, settle conflicts, and approve or reject them." },
    { policy: "direct", text: "Changes pushed from the orbital CLI or Ark Studio are applied at once." },
];

function toValues(space: Space): FormValues {
    return {
        title: space.title ?? "",
        description: space.description ?? "",
        projectId: space.projectId ?? "",
        isHighlighted: !!space.isHighlighted,
    };
}

function UnsavedMark() {
    return <span className="ark-settings-unsaved">Unsaved</span>;
}

/**
 * Space settings (board `Ark-Settings`): the overview fields, the push policy, access and the locked pages of a space on one page,
 * with the danger zone and a save bar. Replaces the old Edit overview / Permissions pair under a 270 px sub-nav. Every call is the old
 * one: `GET/PUT/DELETE api/Spaces/{id}`, the facade's push policy, `GET spaces/{id}/locks`; the figures come from the facade's space
 * (pages) and the proposed-changesets list. Ark.Core decides who may change what: a 403 is shown as a toast.
 */
export function SpaceSettingsPage(props: ISpaceSettingsPageProps) {
    const { spaceId } = useParams();
    const navigate = useNavigate();
    const feedback = useFeedback();
    const spacesSvc = props.spaces ?? spaceService();
    const locksSvc = props.locks ?? lockService();
    const crSvc = props.changeRequests ?? changeRequestService();
    const { focus } = props;

    const [space, setSpace] = useState<SpaceState>({ status: "loading" });
    const [saved, setSaved] = useState<FormValues>();
    const [values, setValues] = useState<FormValues>();
    const [errors, setErrors] = useState<SpaceFieldErrors>({});
    const [submitted, setSubmitted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [locks, setLocks] = useState<LocksState>({ status: "loading" });
    const [pageCount, setPageCount] = useState<number>();
    const [proposed, setProposed] = useState<number>();
    const highlightId = useId();

    /****************************
     * DATA REQUESTS
     *****************************/

    /** The space as loaded; the form starts from it (and starts over after a save). */
    const applySpace = useCallback((next: SpaceState) => {
        setSpace(next);
        if (next.status !== "ready") return;
        const loaded = toValues(next.space);
        setSaved(loaded);
        setValues(loaded);
        setErrors({});
        setSubmitted(false);
    }, []);

    const fetchLocks = useCallback((): Promise<LocksState> => {
        if (!spaceId) return Promise.resolve({ status: "ready", locks: [] });
        return locksSvc.listLocks(spaceId).then(
            (list): LocksState => ({ status: "ready", locks: list }),
            (err): LocksState => lockErrorKind(err) === "identity-unavailable"
                ? { status: "identity-unavailable" }
                : lockErrorKind(err) === "not-found"
                    // a server without lock routes (pre-1b): nothing is locked there
                    ? { status: "ready", locks: [] }
                    : { status: "error", message: lockErrorMessage(err) });
    }, [spaceId, locksSvc]);

    useEffect(() => {
        if (!spaceId) return;
        let alive = true;
        fetchSpaceState(spacesSvc, spaceId).then((next) => alive && applySpace(next));
        fetchLocks().then((next) => alive && setLocks(next));
        return () => {
            alive = false;
        };
    }, [spaceId, spacesSvc, fetchLocks, applySpace]);

    /** Try again (the loader first), or a quiet re-read after a save. */
    const loadSpace = useCallback((quiet?: boolean) => {
        if (!spaceId) return;
        if (!quiet) setSpace({ status: "loading" });
        fetchSpaceState(spacesSvc, spaceId).then(applySpace);
    }, [spaceId, spacesSvc, applySpace]);

    const loadLocks = useCallback(() => {
        setLocks({ status: "loading" });
        fetchLocks().then(setLocks);
    }, [fetchLocks]);

    // the figures of the aside ("This space"); failures leave them out
    useEffect(() => {
        if (!spaceId) return;
        let alive = true;
        crSvc.getSpace(spaceId).then((s) => alive && setPageCount(s.pageCount)).catch(() => undefined);
        crSvc.list(spaceId, "proposed", 200).then((items) => alive && setProposed(items.length)).catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [spaceId, crSvc]);

    const isReady = space.status === "ready";
    useEffect(() => {
        if (!isReady || !focus || focus === "overview") return;
        document.getElementById(`settings-${focus}`)?.scrollIntoView?.({ block: "start" });
    }, [isReady, focus]);

    /****************************
     * FORM
     *****************************/

    const changed = useMemo(() => {
        if (!values || !saved) return [] as (keyof FormValues)[];
        return (Object.keys(fieldLabels) as (keyof FormValues)[]).filter((key) => values[key] !== saved[key]);
    }, [values, saved]);
    const isDirty = changed.length > 0;

    const update = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
        if (!values) return;
        const next = { ...values, [key]: value };
        setValues(next);
        if (submitted) setErrors(validateSpaceFields(next));
    };

    const handleDiscard = useCallback(() => {
        setValues(saved);
        setErrors({});
        setSubmitted(false);
    }, [saved]);

    const handleSave = useCallback(() => {
        if (!values || !spaceId) return;
        setSubmitted(true);
        const found = validateSpaceFields(values);
        setErrors(found);
        if (hasErrors(found)) return;
        setIsSaving(true);
        spacesSvc
            .updateSpace(values.title.trim(), values.description.trim(), values.projectId.trim() || undefined, values.isHighlighted, spaceId)
            .then(() => {
                setIsSaving(false);
                feedback.success("Space edited with success!", "Saved");
                loadSpace(true);
            })
            .catch((err) => {
                setIsSaving(false);
                feedback.error(err, "The space was not saved");
            });
    }, [values, spaceId, spacesSvc, feedback, loadSpace]);

    const handleDelete = useCallback(() => {
        if (!spaceId || isDeleting) return;
        setIsDeleting(true);
        spacesSvc
            .deleteSpace(spaceId)
            .then(() => {
                setIsDeleting(false);
                setIsDeleteOpen(false);
                feedback.success("Space deleted successfully!", "Space deleted");
                navigate("/spaces");
            })
            .catch((err) => {
                setIsDeleting(false);
                feedback.error(err, "The space was not deleted");
            });
    }, [spaceId, isDeleting, spacesSvc, feedback, navigate]);

    /****************************
     * CSS & HTML
     *****************************/

    if (space.status === "not-found") return <Navigate to="/notfound" replace />;

    const title = space.status === "ready" ? space.space.title : undefined;
    const lockCount = locks.status === "ready" ? locks.locks.length : undefined;
    const metaParts = [spaceId ? `Space ${spaceId}` : undefined, pageCount !== undefined ? `${pageCount} pages` : undefined].filter(Boolean);

    let content;
    if (space.status === "loading") {
        content = <LoadingState text="Loading the space…" />;
    } else if (space.status === "identity-unavailable") {
        content = <IdentityUnavailableState onRetry={() => loadSpace()} />;
    } else if (space.status === "error") {
        content = (
            <Callout tone="danger" title="The space could not be loaded">
                <p>{space.message}</p>
                <Button type="link" size="sm" text="Try again" onClick={() => loadSpace()} />
            </Callout>
        );
    } else if (values && spaceId) {
        content = (
            <div className="ark-settings-layout">
                <div className="ark-settings-main">
                    <div className="ark-settings-sections">
                    <section id="settings-overview" className="ark-settings-anchor">
                        <Panel title="Overview">
                            <p className="ark-settings-sub">How the space appears on the Spaces home and in the top bar.</p>
                            <div className="ark-settings-grid">
                                <Field label="Title" required error={errors.title}>
                                    <TextInput value={values.title} onChange={(v) => update("title", v)} hasError={!!errors.title} disabled={isSaving} />
                                </Field>
                                <Field label="Project ID (optional)" error={errors.projectId}
                                    hint="The Azure DevOps project behind this space. It must be a GUID.">
                                    <TextInput value={values.projectId} onChange={(v) => update("projectId", v)} hasError={!!errors.projectId}
                                        disabled={isSaving} className="ark-mono" placeholder="Insert a GUID" />
                                </Field>
                                <div className="ark-settings-wide">
                                    <Field label="Description" required error={errors.description}
                                        hint={changed.includes("description") ? <UnsavedMark /> : undefined}>
                                        <TextArea value={values.description} onChange={(v) => update("description", v)} rows={3}
                                            hasError={!!errors.description} disabled={isSaving} />
                                    </Field>
                                </div>
                            </div>
                            <div className="ark-settings-toggle">
                                <div>
                                    <span className="ark-settings-toggle-label" id={`${highlightId}-label`}>
                                        Highlighted on the Spaces home
                                        {changed.includes("isHighlighted") ? <UnsavedMark /> : null}
                                    </span>
                                    <p className="ark-settings-sub" id={`${highlightId}-hint`}>Highlighted spaces are listed first on the Spaces home.</p>
                                </div>
                                <Toggle value={values.isHighlighted} onToggleChanged={(v) => update("isHighlighted", v)} isDisabled={isSaving}
                                    aria-labelledby={`${highlightId}-label`} aria-describedby={`${highlightId}-hint`} />
                            </div>
                        </Panel>
                    </section>

                    <section id="settings-change-requests" className="ark-settings-anchor">
                        <Panel title="Change requests">
                            <p className="ark-settings-sub">
                                What happens when someone pushes from the orbital CLI or Ark Studio. Edits made in Ark Web are always saved at once.
                            </p>
                            <div className="ark-settings-grid">
                                <PushPolicySelector spaceId={spaceId} service={crSvc} />
                                <dl className="ark-settings-policies">
                                    {policyHelp.map((item) => (
                                        <div key={item.policy}>
                                            <dt><code>{item.policy}</code></dt>
                                            <dd>{item.text}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                            {proposed ? (
                                <Link className="ark-settings-waiting" to={`/spaces/${spaceId}/change-requests`}>
                                    {changeRequestsLabel(proposed)} {proposed === 1 ? "is" : "are"} waiting for review
                                    <Icons.chevronRight size={14} />
                                </Link>
                            ) : null}
                        </Panel>
                    </section>

                    <section id="settings-permissions" className="ark-settings-anchor">
                        <PermissionsSection spaceId={spaceId} />
                    </section>

                    <section id="settings-locked-pages" className="ark-settings-anchor">
                        <LockedPagesSection spaceId={spaceId} locks={locks} onRetry={loadLocks} />
                    </section>

                    <section id="settings-danger-zone" className="ark-settings-anchor">
                        <Panel title="Danger zone" className="ark-settings-danger">
                            <div className="ark-settings-danger-row">
                                <div>
                                    <h3 className="ark-settings-h3">Delete space</h3>
                                    <p className="ark-settings-sub">
                                        Deletes {title} for everyone: its pages, their attachments, locks and change requests. You are asked to
                                        confirm first, and it cannot be undone.
                                    </p>
                                </div>
                                <Button text="Delete space" type="danger" icon={<Icons.trash />} onClick={() => setIsDeleteOpen(true)} />
                            </div>
                        </Panel>
                    </section>
                    </div>

                    <div className={isDirty ? "ark-settings-savebar is-dirty" : "ark-settings-savebar"} role="region" aria-label="Unsaved changes">
                        <span className={isDirty ? "ark-settings-savebar-text is-dirty" : "ark-settings-savebar-text"} aria-live="polite">
                            {isDirty
                                ? `${changed.length} unsaved ${changed.length === 1 ? "change" : "changes"}: ${changed.map((k) => fieldLabels[k]).join(", ")}`
                                : "No unsaved changes"}
                        </span>
                        <div className="ark-row">
                            <Button text="Discard" type="secondary" onClick={handleDiscard} isDisabled={!isDirty || isSaving} />
                            <Button text="Save changes" type="primary" icon={<Icons.check />} onClick={handleSave} isBusy={isSaving}
                                isDisabled={!isDirty} />
                        </div>
                    </div>
                </div>

                <aside className="ark-settings-aside" aria-label="On this page">
                    <nav className="ark-settings-toc" aria-label="Settings sections">
                        <p className="ow-eyebrow">On this page</p>
                        <ul>
                            {sections.map((s) => (
                                <li key={s.id}><a href={`#settings-${s.id}`}>{s.label}</a></li>
                            ))}
                        </ul>
                    </nav>
                    <div className="ark-settings-facts">
                        <p className="ow-eyebrow">This space</p>
                        <dl>
                            <div><dt>Space id</dt><dd>{spaceId}</dd></div>
                            <div><dt>Pages</dt><dd>{pageCount ?? "–"}</dd></div>
                            <div><dt>Locked pages</dt><dd>{lockCount ?? "–"}</dd></div>
                            <div><dt>Proposed changes</dt><dd>{proposed ?? "–"}</dd></div>
                        </dl>
                    </div>
                </aside>
            </div>
        );
    }

    return (
        <Page
            className="ark-settings-page"
            eyebrow={title ? `Space · ${title}` : "Space"}
            meta={metaParts.join(" · ") || undefined}
            title="Space settings"
            tabTitle={["Settings", title]}
            intro="The overview, push policy, access and locked pages of this space, on one page."
        >
            {content}
            <Dialog
                open={isDeleteOpen}
                onClose={() => !isDeleting && setIsDeleteOpen(false)}
                title="Delete space"
                description="Are you sure you want to delete this space?"
                danger
                footer={
                    <>
                        <Button text="Cancel" type="secondary" onClick={() => setIsDeleteOpen(false)} isDisabled={isDeleting} />
                        <Button text="Delete space" type="danger" onClick={handleDelete} isBusy={isDeleting} />
                    </>
                }
            >
                <p className="ark-settings-sub">
                    {title ? `“${title}”` : "The space"}, its pages, their attachments, locks and change requests are deleted for everyone.
                </p>
            </Dialog>
        </Page>
    );
}
